<script lang="ts">
    import { onDestroy, onMount, tick } from 'svelte'
    import MaskInput from 'svelte-input-mask/MaskInput.svelte'
    import { get, derived as sderived, writable } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { isEqual, orderBy } from 'lodash-es'
    import * as yup from 'yup'

    import { iconAddressBook } from 'hydria/icons'
    import { gettext as _, resolve, tgettext, type TGettext } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { session } from 'hydria/services/session'
    import { escapeHtml } from 'hydria/utils/html'
    import { removeAccents } from 'hydria/utils/strings'

    import { cprCategories } from 'wdom/services/contacts'
    import type { IAddress, IAddressGroup, ICprCategory } from 'wdom/services/contacts'
    import { canadianProvinceIndex, canadianProvinces, countries, countryIndex, usStateIndex, usStates } from 'wdom/services/country_data'

    import type { IContactGroup } from './interfaces'

    import Dropdown from 'hydria/components/Dropdown.svelte'

    // The actual "initial, saved" contacts, on initialization, else these are "edits" from the parent component.

    interface Props {
        context: 'domain' | 'cart' | 'account'
        // e.g. "undo" or "load revision from other domain" or such
        contactGroup: IContactGroup
        inlineMode?: boolean
        alignRight?: boolean
        addresses?: IAddressGroup[] | null
        autoLg?: boolean | string

        updateGroupValidity?: (cg: IContactGroup) => IContactGroup

        close: () => void
        save: (cg: IContactGroup) => void
    }

    let { context, contactGroup: inContactGroup, inlineMode = false, alignRight = false, addresses = null, autoLg = false, updateGroupValidity, close, save }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let contactGroup: IContactGroup = $state(inContactGroup)
    // onMount(() => {
    //     contactGroup = inContactGroup
    // })
    // $inspect('contact editor, contactGroup = ', contactGroup)

    // To force a refresh of the validation when certain conditions are met
    const revalidate = writable(1)
    let cprInit = false
    function cprReady() {
        if ($cprCategories != null) return true
        if (!cprInit) {
            cprInit = true
            unsubs.push(
                countries.subscribe((v) => {
                    if (v != null) {
                        revalidate.update((rv) => rv + 1)
                    }
                }),
            )
        }
        return false
    }

    let countryInit = false
    function countriesReady() {
        if ($countries != null) return true
        if (!countryInit) {
            countryInit = true
            unsubs.push(
                countries.subscribe((v) => {
                    if (v != null) {
                        revalidate.update((rv) => rv + 1)
                    }
                }),
            )
        }
        return false
    }
    let caSpInit = false
    function caSpReady() {
        if ($canadianProvinces != null) return true
        if (!caSpInit) {
            caSpInit = true
            unsubs.push(
                canadianProvinces.subscribe((v) => {
                    if (v != null) {
                        revalidate.update((rv) => rv + 1)
                    }
                }),
            )
        }
        return false
    }
    let usSpInit = false
    function usSpReady() {
        if ($usStates != null) return true
        if (!usSpInit) {
            usSpInit = true
            unsubs.push(
                usStates.subscribe((v) => {
                    if (v != null) {
                        revalidate.update((rv) => rv + 1)
                    }
                }),
            )
        }
        return false
    }

    // ##############################################################  Load CPR lists
    const sortedCprCategories = sderived(cprCategories, ($cprCategories) => {
        if ($cprCategories == null) return null
        return orderBy($cprCategories, [
            (cc) => {
                if (cc.code == 'CCT') return 0
                if (cc.code == 'CCO') return 1
                return cc.classification == 'I' ? 2 : 3
                // if (cc.code == 'CCT') return 0
                // if (cc.code == 'CCO') return 10
                // return cc.classification == 'I' ? 1 : 11
            },
        ])
    })

    let cprIndex: Readable<{ [key: string]: ICprCategory }> = sderived(cprCategories, ($cprCategories) => {
        let indx = {}
        if ($cprCategories != null) {
            for (const cc of $cprCategories) {
                indx[cc.code] = cc
            }
        }
        return indx
    })

    interface ICprGroup {
        code: 'group'
        classification: 'NI' | 'I'
    }

    const sortedCprCategoriesSepped = sderived(sortedCprCategories, ($sortedCprCategories) => {
        if ($sortedCprCategories == null) return null
        const cats: (ICprCategory | ICprGroup)[] = []
        let lastType: string | undefined
        for (const [i, cc] of $sortedCprCategories.entries()) {
            if (i < 2) {
                cats.push(cc)
            } else {
                if (lastType !== cc.classification) {
                    cats.push({
                        code: 'group',
                        classification: cc.classification,
                    })
                    lastType = cc.classification
                }
                cats.push(cc)
            }
        }
        return cats
    })
    const sortedCprCategoriesIndividualSepped = sderived(sortedCprCategoriesSepped, ($cprCats) => {
        if ($cprCats == null) return null
        return $cprCats.filter((cc) => cc.classification == 'I')
    })

    // Fields valid or not. Needed for async validation, else things would get way too verbose in the HTML below.
    let yupValidFields: { [key: string]: boolean } = $state({})
    let streetLines: Writable<string[]> = writable([])

    let nameInvalidReason: TGettext | undefined = $state(undefined)

    // IMPORTANT: this is in ADDITION to the main validation in ContactGroupEditor
    // (which only checks for presence and requirement).
    function setupSchema() {
        let fields = {}
        const cgFields: Set<string> = contactGroup.fields ? contactGroup.fields : new Set()
        const cgReqFields: Set<string> = contactGroup.requiredFields ? contactGroup.requiredFields : new Set()
        // for (const f of ['name', 'org', 'city', 'email', 'postal_code', 'voice', 'fax', 'cira_cpr_category', 'cira_prefered_lang', 'mobile', 'title', 'comment', 'accounting_comment']) {
        for (const f of cgFields) {
            // if (cgFields.has(f)) {
            fields[f] = yup.string().nullable()
            switch (f) {
                case 'name':
                    fields[f] = fields[f].max(255)
                    if (contactGroup.keepFields && contactGroup.keepFields.has('cira_cpr_category') && contactGroup.type == 'registrant') {
                        // CIRA name needs 2 words if this is a person
                        fields[f] = fields[f].test('isValidRegistrant', (value: string) => {
                            if (!cprReady()) {
                                nameInvalidReason = undefined
                                return false
                            }
                            if (!contactGroup.edit.cira_cpr_category || ($cprIndex[contactGroup.edit.cira_cpr_category] != null && $cprIndex[contactGroup.edit.cira_cpr_category].classification == 'I')) {
                                const words = value.split(/\s+/)
                                if (words.length < 2) {
                                    nameInvalidReason = tgettext('Requires at least two words')
                                    return false
                                }
                            }
                            nameInvalidReason = undefined
                            return true
                        })
                    } else {
                        nameInvalidReason = undefined
                    }
                    break
                case 'org':
                case 'city':
                case 'title':
                case 'comment':
                case 'accounting_comment':
                    fields[f] = fields[f].max(255)
                    break
                case 'email':
                    fields[f] = fields[f].email().max(320)
                    break
                case 'postal_code':
                    fields[f] = fields[f].max(40)
                    if (cgFields.has('country'))
                        fields[f] = fields[f].when('country', ([country]: [string], lschema: yup.StringSchema) => {
                            if (country == 'CA') return lschema.matches(/^[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]$/)
                            return lschema
                        })
                    break
                case 'voice':
                case 'fax':
                case 'mobile':
                    fields[f] = fields[f].max(50).when('country', ([country]: [string], lschema: yup.StringSchema) => {
                        if (country == 'CA' || country == 'US') return lschema.matches(/^(\+1\.\d{10}(x\d+)?)?$/)
                        return lschema.matches(/^((\+\d+\.\d+)(x\d{1,10})?)?$/)
                    })
                    readPhoneNumber(f)
                    break
                case 'cira_prefered_lang':
                    fields[f] = fields[f].test('isValidLg', (value: string) => value == 'fr' || value == 'en')
                    break
                case 'cira_cpr_category':
                    fields[f] = fields[f].test('isValidCpr', (value: string) => {
                        if (!cprReady()) return false
                        return ($cprIndex[value] != null && $cprIndex[value].classification == 'I') || contactGroup.type == 'registrant'
                        // for (const cpr of get(cprCategories)) {
                        //     if (cpr.code == value) return true
                        // }
                        // return false
                    })
                    break
            }
            if ((f == 'voice' || f == 'mobile') && cgReqFields.has('any-phone')) {
                const other = f == 'voice' ? 'mobile' : 'voice'
                fields[f] = fields[f].test(
                    (function (me: string, other: string) {
                        return function (value: string) {
                            // @ts-expect-error
                            const ovalue = this.parent[other]
                            if (!ovalue) return value != null && value != ''
                            return true
                        }
                    })(f, other),
                )
            } else {
                fields[f] = fields[f].test({
                    name: `req-${f}`,
                    message: '${path} is required',
                    params: {},
                    test: ((f: string) => {
                        return (value: any) => !cgReqFields.has(f) || !!value
                    })(f),
                })
            }
        }

        if (cgFields.has('country')) {
            fields['country'] = yup
                .string()
                .min(2)
                .max(2)
                .test('isValidCountry', (value) => {
                    if (!countriesReady()) {
                        return false
                    }
                    if (value == null) return false
                    return get(countryIndex)[value] != null
                })
            if (cgReqFields.has('country')) fields['country'] = fields['country'].required()
        }

        if (cgFields.has('sp')) {
            fields['sp'] = yup
                .string()
                .max(255)
                .when('country', ([country], lschema: yup.StringSchema) => {
                    if (country == 'CA') {
                        return lschema.test('isValidCaProvince', (value) => {
                            if (!caSpReady()) return false
                            if (value == null) return false
                            return get(canadianProvinceIndex)[value] != null
                        })
                    } else if (country == 'US') {
                        return lschema.test('isValidUsState', (value) => {
                            if (!usSpReady()) return false
                            if (value == null) return false
                            return get(usStateIndex)[value] != null
                        })
                    } else {
                        return lschema
                    }
                })
            if (cgReqFields.has('sp')) fields['sp'] = fields['sp'].required()
        }

        if (cgFields.has('street')) {
            // We actually want a max of 2, but some old entry may have up to 3 lines
            fields['street'] = yup.array().of(yup.string()).max(3)
            if (cgReqFields.has('street')) fields['street'] = fields['street'].min(1)
            readStreet()
        }

        for (const field of Object.keys(fields)) {
            yupValidFields[field] = false
        }

        return yup.object().shape(fields)
    }

    interface PhoneNumber {
        int: string
        num: string
        ext: string
    }
    const phoneNumbers: { [key: string]: PhoneNumber } = $state({})
    const schema = setupSchema()

    function readPhoneNumber(field: string) {
        const enc = contactGroup.edit[field]
        // This is used in MaskInput, which doesn't support nulls
        const pieces = {
            int: '',
            num: '',
            ext: '',
        }
        if (enc != null) {
            const m = enc.match(/^\+(?<int>\d+)\.(?<num>\d+)(?:x(?<ext>\d+))?$/)
            if (m) {
                // Object.assign(pieces, m.groups)
                for (const [k, v] of Object.entries(m.groups)) {
                    pieces[k] = v ? v : ''
                }
            }
        }
        phoneNumbers[field] = pieces
    }
    function writePhoneNumber(field: string) {
        let value = ''
        let inc = {}
        for (const [k, v] of Object.entries(phoneNumbers[field])) {
            inc[k] = v != null ? v.trim() : null
        }
        if (inc['int'] && inc['num']) {
            value = `+${inc['int']}`
            value += `.${inc['num']}`
            if (inc['ext']) {
                value += `x${inc['ext']}`
            }
        }
        contactGroup.edit[field] = value
    }
    function writePhoneNumbers() {
        for (const field of Object.keys(phoneNumbers)) {
            writePhoneNumber(field)
        }
    }

    function readStreet() {
        let sl: string[] = []
        if (contactGroup.edit.street != null) {
            for (let line of contactGroup.edit.street) {
                line = line.trim()
                if (line) {
                    sl.push(line)
                }
            }
        }
        if (sl.length < 2) {
            // Leave 1 line empty, up to the max
            sl.push('')
            // for (let i = sl.length; i < 2; i++) {
            //     sl.push('')
            // }
        }
        if (!isEqual($streetLines, sl)) streetLines.set(sl)
    }
    function writeStreet() {
        const lines: string[] = []
        for (let line of $streetLines) {
            line = line.trim()
            if (line) {
                lines.push(line)
            }
        }
        if (!isEqual(contactGroup.edit.street, lines)) {
            contactGroup.edit.street = lines
            readStreet()
        }
    }

    async function validateFormField(key: string, form: any) {
        try {
            await schema.validateAt(key, form)
            yupValidFields[key] = true
        } catch {
            yupValidFields[key] = false
        }
        return schema.isValid
    }

    let formIsValid = $state(false)
    function validateForm(edit: any) {
        for (const key of Object.keys(yupValidFields)) {
            validateFormField(key, contactGroup.edit)
        }
        schema
            .validate(contactGroup.edit)
            .then((v) => {
                formIsValid = true
            })
            .catch((v) => {
                formIsValid = false
            })

        if (updateGroupValidity) {
            const vcg = updateGroupValidity($state.snapshot(contactGroup))
            if (!isEqual((vcg.invalidFields as any).contextual, (contactGroup.invalidFields as any).contextual)) {
                ;(contactGroup.invalidFields as any).contextual = (vcg.invalidFields as any).contextual
            }
        }
    }

    $effect(() => {
        contactGroup && $revalidate && validateForm(contactGroup.edit)
    })

    $effect(() => {
        phoneNumbers && writePhoneNumbers()
    })
    $effect(() => {
        $streetLines && writeStreet()
    })

    let dropdownOpen: null | 'cpr' | 'country' | 'sp' = $state(null)

    // MaskInput / 'input-core' can't handle null values. I don't want to import the whole code and fix it, so..
    let postalCodeForMask = $derived(!contactGroup || contactGroup.edit.postal_code == null ? '' : contactGroup.edit.postal_code)

    // ############## Default Values, when empty ########################
    // FIXME: Should probably use the main contact address by default..

    $effect(() => {
        if (contactGroup && contactGroup.fields && (contactGroup.fields as Set<string>).has('country') && (contactGroup.fields as Set<string>).has('sp') && contactGroup.edit.country === 'CA' && !contactGroup.edit.sp) contactGroup.edit.sp = 'QC'
    })
    $effect(() => {
        if (contactGroup && contactGroup.fields && (contactGroup.fields as Set<string>).has('country') && ['US', 'CA'].includes(contactGroup.edit.country)) {
            for (const field of ['voice', 'fax', 'mobile']) {
                if (phoneNumbers[field] != null && !phoneNumbers[field].int) phoneNumbers[field].int = '1'
            }
        }
    })
    $effect(() => {
        if (contactGroup && contactGroup.fields && (contactGroup.fields as Set<string>).has('country') && !contactGroup.edit.country) contactGroup.edit.country = 'CA'
    })

    function importFromAddressText(lg: string) {
        // Pour bypasser le problème de parsing de {... `... ${$_(..)}}..`..} de Babel Python
        return $_('Import a billing address')
    }

    function loadFromAddressBook(id: string) {
        let address: IAddress | undefined

        if (addresses == null) return
        for (const agroup of addresses) {
            if (agroup.id == id) {
                address = agroup.source
                break
            }
        }
        if (!address) return

        for (const field of ['name', 'org', 'city', 'email', 'postal_code', 'voice', 'fax', 'street']) {
            if ((contactGroup.fields as Set<string>).has(field)) {
                if (field == 'street') {
                    contactGroup.edit[field] = address[field] && address[field].length ? [...address[field]] : []
                }
                contactGroup.edit[field] = address[field] ? address[field] : ''
                if (field == 'voice' || field == 'fax') {
                    readPhoneNumber(field)
                }
            }
        }

        readStreet()
    }

    $effect(() => {
        if (contactGroup && !inlineMode && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if ((!contactGroup || inlineMode) && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    onDestroy(() => {
        if (!inlineMode && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })

    function countryFiltering(items: any[], text: string) {
        return items.filter((item) => {
            return removeAccents(item.code.toLocaleLowerCase()).includes(text) || removeAccents(item.name.toLocaleLowerCase()).includes(text)
        })
    }
    function cprFiltering(items: any[], text: string) {
        return items.filter((item) => {
            if (item.code == 'group') return false
            return item.code.toLocaleLowerCase().includes(text) || removeAccents(item[`description_${$lg}`].toLocaleLowerCase()).includes(text)
        })
    }

    function fakeStringSet(i: Set<string> | undefined) {
        return i as Set<string>
    }
    function fakeObjStringSet(i: { [key: string]: Set<string> } | undefined) {
        return i as { [key: string]: Set<string> }
    }
    function neverNull(x: any) {
        return x as any
    }

    let elCategory: HTMLInputElement | undefined = $state()
    let dialogElement: HTMLElement | undefined = $state()

    onMount(() => {
        if (fakeStringSet(contactGroup.fields).has('cira_cpr_category') && autoLg && contactGroup.edit.cira_prefered_lang == null) {
            if (autoLg == 'fr' || autoLg == 'en') {
                contactGroup.edit.cira_prefered_lang = autoLg
            } else if (session.miscData.prefered_lg) {
                if (session.miscData.force_prefered_lg) {
                    contactGroup.edit.cira_prefered_lang = session.miscData.prefered_lg
                } else {
                    contactGroup.edit.cira_prefered_lang = $lg as 'fr' | 'en'
                }
            }
        }

        tick().then(() => setFocussed())
    })
    let focussed: boolean = false
    function setFocussed(els?: any) {
        if (focussed) return
        focussed = true
        tick().then(() => {
            if (elCategory) elCategory.focus()
            else if (dialogElement) {
                const el = dialogElement.querySelector('input[type=text]')
                if (el) (el as HTMLInputElement).focus()
            }
        })
    }
</script>

<svelte:body
    onclick={(e) => {
        if (dropdownOpen) dropdownOpen = null
    }}
/>

{#if contactGroup}
    <div id="contact-edit-parent" class:modal-open={inlineMode}>
        <div class="modal contact-modal" class:modal-right={alignRight} role="dialog" transition:fade={{ duration: 250 }} class:embedded-contact-modal={inlineMode}>
            <div class="modal-dialog" class:modal-lg={!inlineMode} bind:this={dialogElement}>
                <div class="modal-content">
                    <div class="modal-header text-bg-primary">
                        <div>
                            {#if context == 'domain'}{$_('Contact information')}
                            {:else if context == 'cart'}{$_('Address information')}
                            {:else if context == 'account'}{$_('Billing address')}
                            {/if}
                        </div>
                    </div>

                    <form
                        id="cm-form"
                        novalidate
                        onsubmit={(e) => {
                            e.preventDefault()
                        }}
                    >
                        <div class="modal-body d-grid gap-2">
                            {#if fakeStringSet(contactGroup.fields).has('cira_cpr_category')}
                                <div class="form-group row">
                                    <label for="ce-cira_prefered_lang" class="col-sm-3 col-form-label text-end">{$_('Preferred language')}</label>
                                    <div class="col-sm-9 d-flex align-items-center gap-3" class:text-danger={fakeObjStringSet(contactGroup.invalidFields).contextual.has('cira_prefered_lang') || !yupValidFields['cira_prefered_lang']}>
                                        <label class="radio-inline"><input type="radio" value="fr" required={fakeStringSet(contactGroup.requiredFields).has('cira_prefered_lang')} bind:group={contactGroup.edit.cira_prefered_lang} /> {$_('French')}</label>
                                        <label class="radio-inline"><input type="radio" value="en" required={fakeStringSet(contactGroup.requiredFields).has('cira_prefered_lang')} bind:group={contactGroup.edit.cira_prefered_lang} /> {$_('English')}</label>
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('cira_cpr_category')}
                                <div class="form-group row">
                                    <label for="ce-cira_cpr_category" class="col-sm-3 col-form-label text-end">{$_('Presence category')}</label>
                                    <div class="col-sm-9">
                                        {#if (contactGroup.type == 'registrant' ? $sortedCprCategoriesSepped : $sortedCprCategoriesIndividualSepped) == null}
                                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                        {:else}
                                            <Dropdown
                                                items={neverNull(contactGroup.type == 'registrant' ? $sortedCprCategoriesSepped : $sortedCprCategoriesIndividualSepped)}
                                                placeholder={$_('Presence category')}
                                                textIsHtml={true}
                                                text={(cat) => {
                                                    if (cat.code == 'group') {
                                                        if (cat.classification == 'I') return $_('Individual')
                                                        return $_('Non-individual')
                                                    }
                                                    const txt = cat[`description_${$lg}`]
                                                    return `<span class="badge bg-secondary" style="min-width: 2.5rem;">${cat.code}</span><span class="ms-3">${txt}</span>`
                                                }}
                                                id={'code'}
                                                alignRight={true}
                                                block={true}
                                                itemFiltering={cprFiltering}
                                                btnClasses={fakeObjStringSet(contactGroup.invalidFields).contextual.has('cira_cpr_category') || !yupValidFields['cira_cpr_category'] ? 'btn-outline-danger' : 'btn-outline-secondary'}
                                                selected={contactGroup.edit.cira_cpr_category}
                                                selectable={(item) => item.code != 'group'}
                                                itemClass={(item, i) => (neverNull(i) >= 2 && item.code != 'group' ? 'indented' : '')}
                                                onSelect={(e) => {
                                                    contactGroup.edit.cira_cpr_category = e
                                                }}
                                                onButtonElement={(e) => {
                                                    elCategory = e as HTMLInputElement
                                                }}
                                            />
                                        {/if}
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('title')}
                                <div class="form-group row">
                                    <label for="ce-title" class="col-sm-3 col-form-label text-end">{$_('Title')}</label>
                                    <div class="col-sm-9">
                                        <input type="text" id="ce-title" class="form-control" bind:value={contactGroup.edit.title} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('title') || !yupValidFields['title']} />
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('comment')}
                                <div class="form-group row">
                                    <label for="ce-comment" class="col-sm-3 col-form-label text-end">{$_('Comment')}</label>
                                    <div class="col-sm-9">
                                        <input type="text" id="ce-comment" class="form-control" bind:value={contactGroup.edit.comment} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('comment') || !yupValidFields['comment']} />
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('accounting_comment')}
                                <div class="form-group row">
                                    <label for="ce-accounting_comment" class="col-sm-3 col-form-label text-end">{$_('Accounting')}</label>
                                    <div class="col-sm-9">
                                        <input type="text" id="ce-accounting_comment" class="form-control" bind:value={contactGroup.edit.accounting_comment} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('accounting_comment') || !yupValidFields['accounting_comment']} />
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('name')}
                                <div class="form-group row align-items-center">
                                    <label for="ce-name" class="col-sm-3 col-form-label text-end">{$_('Full name')}</label>
                                    <div class="col-sm-9">
                                        <input type="text" id="ce-name" class="form-control" bind:value={contactGroup.edit.name} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('name') || !yupValidFields['name']} />
                                        {#if nameInvalidReason}
                                            <div class="invalid-feedback">{$resolve(nameInvalidReason)}</div>
                                        {/if}
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('org')}
                                <div class="form-group row">
                                    <label for="ce-org" class="col-sm-3 col-form-label text-end">{$_('Organization')}</label>
                                    <div class="col-sm-9">
                                        <input type="text" id="ce-org" class="form-control" bind:value={contactGroup.edit.org} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('org') || !yupValidFields['org']} />
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('email')}
                                <div class="form-group row">
                                    <label for="ce-email" class="col-sm-3 col-form-label text-end">{$_('Email address')}</label>
                                    <div class="col-sm-9">
                                        <input type="email" id="ce-email" class="form-control" bind:value={contactGroup.edit.email} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('email') || !yupValidFields['email']} />
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('street')}
                                <div class="form-group row">
                                    <label for="ce-street-0" class="col-sm-3 col-form-label text-end">{$_('Address')}</label>
                                    <div class="col-sm-9 d-grid gap-1">
                                        {#each $streetLines as line, i}
                                            <input type="text" id="ce-street-{i}" class="form-control" bind:value={$streetLines[i]} placeholder={i ? $_('line {n}', { n: i + 1 }) : null} oninput={() => writeStreet()} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('street') || !yupValidFields['street']} />
                                        {/each}
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('city')}
                                <div class="form-group row">
                                    <label for="ce-city" class="col-sm-3 col-form-label text-end">{$_('City')}</label>
                                    <div class="col-sm-9">
                                        <input type="text" id="ce-city" class="form-control" bind:value={contactGroup.edit.city} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('city') || !yupValidFields['city']} />
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('country')}
                                <div class="form-group row">
                                    <label for="ce-country" class="col-sm-3 col-form-label text-end">{$_('Country')}</label>
                                    <div class="col-sm-9">
                                        {#if $countries == null}
                                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                        {:else}
                                            <Dropdown
                                                items={$countries}
                                                placeholder={$_('Select a country')}
                                                text={'name'}
                                                id={'code'}
                                                alignRight={true}
                                                block={true}
                                                itemFiltering={countryFiltering}
                                                btnClasses={fakeObjStringSet(contactGroup.invalidFields).contextual.has('country') || !yupValidFields['country'] ? 'btn-outline-danger' : 'btn-outline-secondary'}
                                                selected={contactGroup.edit.country}
                                                onSelect={(e) => {
                                                    contactGroup.edit.country = e
                                                    if (e == 'CA') {
                                                        for (const pfield of ['voice', 'mobile', 'fax']) {
                                                            if (phoneNumbers[pfield] != null) {
                                                                phoneNumbers[pfield].int = '1'
                                                                writePhoneNumber(pfield)
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        {/if}
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('sp')}
                                <div class="form-group row">
                                    <label for="ce-sp" class="col-sm-3 col-form-label text-end">{contactGroup.edit.country === 'US' ? $_('State') : $_('Province')}</label>
                                    <div class="col-sm-9">
                                        {#if contactGroup.edit.country == 'CA' || contactGroup.edit.country == 'US'}
                                            <div class="btn-group dropdown dropdown-block">
                                                {#if (contactGroup.edit.country == 'CA' ? $canadianProvinces : $usStates) == null}
                                                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                                {:else}
                                                    <Dropdown
                                                        items={neverNull(contactGroup.edit.country == 'CA' ? $canadianProvinces : $usStates)}
                                                        placeholder={contactGroup.edit.country == 'CA' ? $_('Select a province') : $_('Select a state')}
                                                        text={'name'}
                                                        id={'code'}
                                                        alignRight={true}
                                                        block={true}
                                                        itemFiltering={countryFiltering}
                                                        btnClasses={fakeObjStringSet(contactGroup.invalidFields).contextual.has('sp') || !yupValidFields['sp'] ? 'btn-outline-danger' : 'btn-outline-secondary'}
                                                        selected={contactGroup.edit.sp}
                                                        onSelect={(e) => {
                                                            contactGroup.edit.sp = e
                                                        }}
                                                    />
                                                {/if}
                                            </div>
                                        {:else}
                                            <input type="text" class="form-control" bind:value={contactGroup.edit.sp} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has('sp') || !yupValidFields['sp']} />
                                        {/if}
                                    </div>
                                </div>
                            {/if}

                            {#if fakeStringSet(contactGroup.fields).has('postal_code')}
                                <div class="form-group row">
                                    <label for="ce-postal_code" class="col-sm-3 col-form-label text-end">{contactGroup.edit.country == 'US' ? $_('Zip code') : $_('Postal code')}</label>
                                    <div class="col-sm-9">
                                        {#if contactGroup.edit.country == 'CA'}
                                            <MaskInput
                                                class="form-control {fakeObjStringSet(contactGroup.invalidFields).contextual.has('postal_code') || !yupValidFields['postal_code'] ? 'is-invalid' : ''}"
                                                id="ce-postal_code"
                                                alwaysShowMask={false}
                                                maskString="A0A 0A0"
                                                mask={'a0a 0a0'}
                                                placeholder={'A0A 0A0'}
                                                bind:value={postalCodeForMask}
                                                on:change={({ detail }) => {
                                                    contactGroup.edit.postal_code = detail.inputState.unmaskedValue ? detail.inputState.unmaskedValue.toUpperCase() : ''
                                                }}
                                            />
                                        {:else}
                                            <input type="text" id="ce-postal_code" class="form-control" bind:value={contactGroup.edit.postal_code} />
                                        {/if}
                                    </div>
                                </div>
                            {/if}

                            {#each ['voice', 'mobile', 'fax'] as field}
                                {#if fakeStringSet(contactGroup.fields).has(field)}
                                    <div class="form-group row">
                                        <label for="ce-{field}" class="col-sm-3 col-form-label text-end">{field == 'voice' ? $_('Phone number') : field == 'fax' ? $_('Fax number') : $_('Mobile number')}</label>
                                        <div class="col-sm-9">
                                            <div class="row">
                                                <div class="phone-col col-sm-3">
                                                    <div class="input-group" data-tooltip={$_('Country code')}>
                                                        <span class="input-group-text">+</span>
                                                        <input type="text" class="form-control phone-int-code" id="ce-{field}-int" bind:value={phoneNumbers[field].int} placeholder="1" disabled={['CA', 'US'].includes(contactGroup.edit.country)} oninput={() => writePhoneNumber(field)} />
                                                    </div>
                                                </div>
                                                <div class="phone-col col-sm-5">
                                                    <div class="input-group" data-tooltip={contactGroup.edit.country == 'CA' ? $_('Phone number') : $_('Phone number')}>
                                                        <span class="input-group-text">.</span>
                                                        {#if contactGroup.edit.country == 'CA'}
                                                            <MaskInput
                                                                class="form-control {fakeObjStringSet(contactGroup.invalidFields).contextual.has(field) || !yupValidFields[field] ? 'is-invalid' : ''}"
                                                                alwaysShowMask={false}
                                                                placeholder={$lg == 'fr' ? '000 000-0000' : '000-000-0000'}
                                                                mask={$lg == 'fr' ? '000 000-0000' : '000-000-0000'}
                                                                bind:value={phoneNumbers[field].num}
                                                                on:change={({ detail }) => {
                                                                    phoneNumbers[field].num = detail.inputState.unmaskedValue
                                                                    writePhoneNumber(field)
                                                                }}
                                                            />
                                                        {:else}
                                                            <input type="text" class="form-control phone-number" class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has(field) || !yupValidFields[field]} id="ce-{field}" bind:value={phoneNumbers[field].num} oninput={() => writePhoneNumber(field)} />
                                                        {/if}
                                                    </div>
                                                </div>
                                                <div class="phone-col col-sm-4">
                                                    <div class="input-group" data-tooltip={$_('Extension')}>
                                                        <span class="input-group-text">x</span>
                                                        <input type="text" class="form-control phone-number" id="ce-{field}-ext" bind:value={phoneNumbers[field].ext} placeholder="ext" oninput={() => writePhoneNumber(field)} class:is-invalid={fakeObjStringSet(contactGroup.invalidFields).contextual.has(field) || !yupValidFields[field]} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                {/if}
                            {/each}
                        </div>
                    </form>

                    <div class="modal-footer text-light">
                        <div class="btn-toolbar d-flex gap-2 justify-content-end">
                            {#if addresses && addresses.length > 1}
                                <div>
                                    <Dropdown
                                        items={addresses}
                                        placeholderHtml={`${iconAddressBook}&nbsp;&nbsp;${importFromAddressText($lg)}`}
                                        text={(t) => {
                                            let text = escapeHtml($resolve(t.title))
                                            if (t.source.comment) {
                                                const lines = t.source.comment
                                                    .split('\n')
                                                    .map((s) => escapeHtml(s))
                                                    .join('<br />')
                                                text = `${text}<span class="comment">${lines}</span>`
                                            }
                                            return text
                                        }}
                                        alignTop={true}
                                        textIsHtml={true}
                                        selected={null}
                                        onSelect={(e) => {
                                            loadFromAddressBook(e.id)
                                        }}
                                    />
                                </div>
                            {:else if addresses && addresses.length > 0}
                                <button type="button" class="btn btn-outline-secondary" onclick={() => loadFromAddressBook(addresses[0].id)}>{$_('Import your billing address')}</button>
                            {/if}
                            <button class="btn btn-outline-secondary" type="button" onclick={() => close()}>{$_('Cancel')}</button>
                            <button class="btn btn-primary" type="button" onclick={() => save(contactGroup)} disabled={isEqual(contactGroup.edit, contactGroup.source) || !formIsValid}>{$_('Save')}</button>
                        </div>
                    </div>
                </div>
            </div>
            {#if !inlineMode}
                <div
                    class="modal-backdrop"
                    aria-hidden="true"
                    onclick={() => {
                        close()
                    }}
                ></div>
            {/if}
        </div>
    </div>
{/if}
